import { useState, useEffect } from 'react'
import {
  referralGetDetails,
  referralRedeem,
  referralGetWithEnabledDetails,
} from '../redux/modules/friends'
import { normalizeErrorResponse } from '../util/errorUtils'
import { pushAnalyticsEvent } from '../util/gtmUtils'
import { useSelector } from 'react-redux'
import currency from 'currency.js'

/**
 * Hook to redeem a dual use referral.
 * @param {*} code the referral code
 */
export const useReferralRedemption = code => {
  const [referralCode, setReferralCode] = useState(code)
  const [redemptionResult, setRedemptionResult] = useState(null)
  const [redemptionError, setError] = useState(null)
  const [redemptionLoading, setLoading] = useState(null)
  const user = useSelector(state => state.user)

  useEffect(() => {
    setReferralCode(code)
  }, [code])

  useEffect(() => {
    let isMounted = true

    if (
      referralCode &&
      typeof referralCode === 'string' &&
      user?.isAuthenticated
    ) {
      setLoading(true)
      referralRedeem(referralCode)
        .then(r => {
          if (r?.type === 'NEW_USER') {
            pushAnalyticsEvent('Referral', 'Redeemed')
          } else if (r?.type === 'DAILY') {
            pushAnalyticsEvent('Referral-Daily', 'Redeemed')
          }
          if (isMounted) {
            setRedemptionResult(r)
          }
        })
        .catch(err => {
          pushAnalyticsEvent('Referral', 'Referral Error')
          if (isMounted) {
            setError(normalizeErrorResponse(err))
          }
        })
        .finally(() => {
          if (isMounted) {
            setLoading(false)
          }
        })
    }

    return () => {
      isMounted = false
    }
  }, [referralCode, user])

  return {
    redemptionResult,
    redemptionError,
    redemptionLoading,
    setReferralCode,
  }
}

/**
 * Get details about a referral code
 * @param {*} code the referral code
 */
export const useReferralDetails = code => {
  const [referralCode, setReferralCode] = useState(code)
  const [referralDetails, setReferralDetails] = useState(null)
  const [referralError, setError] = useState(null)
  const [referralLoading, setLoading] = useState(null)

  useEffect(() => {
    setReferralCode(code)
  }, [code])

  useEffect(() => {
    let isMounted = true
    if (referralCode && typeof referralCode === 'string') {
      setLoading(true)
      referralGetDetails(referralCode)
        .then(r => {
          if (isMounted) {
            setError(null)
            setReferralDetails(r)
          }
        })
        .catch(err => {
          if (isMounted) {
            setError(normalizeErrorResponse(err))
          }
        })
        .finally(() => {
          if (isMounted) {
            setLoading(false)
          }
        })
    }

    return () => {
      isMounted = false
    }
  }, [referralCode])

  return { referralDetails, referralError, referralLoading, setReferralCode }
}

/**
 * Get the current users referral code, alongside enabled details and reward text.
 */
export const useMyReferralWithEnabledDetails = () => {
  const [referralAndEnabledDetails, setReferralAndEnabledDetails] = useState()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState()

  const referralText = `Invite your work friends to Foodsby and you’ll both get a ${currency(
    referralAndEnabledDetails?.referralEnabledDetails?.rewardAmountPennies /
      100,
    { formatWithSymbol: true },
  ).format()} credit when they
  place their first order and it's delivered.`

  const dailyReferralText = `Share your daily link and earn a ${currency(
    referralAndEnabledDetails?.dailyLinkEnabledDetails
      ?.referrerRewardAmountPennies / 100,
    { formatWithSymbol: true },
  ).format()} credit for each co-worker that orders today.
  Your co-workers get a ${currency(
    referralAndEnabledDetails?.dailyLinkEnabledDetails
      ?.redeemerRewardAmountPennies / 100,
    { formatWithSymbol: true },
  ).format()} credit when they order from your link.`

  const referralCopiedText = `Have you tried Foodsby? Sign up with my link and you will get ${currency(
    referralAndEnabledDetails?.referralEnabledDetails?.rewardAmountPennies /
      100,
    { formatWithSymbol: true },
  ).format()} off your first order: ${
    referralAndEnabledDetails?.referral?.shareUrl
  }`

  useEffect(() => {
    let isMounted = true
    setLoading(true)
    referralGetWithEnabledDetails()
      .then(result => {
        if (isMounted) {
          setReferralAndEnabledDetails(result)
          setLoading(false)
        }
      })
      .catch(error => {
        if (isMounted) {
          setError(normalizeErrorResponse(error))
          setLoading(false)
        }
      })

    return () => {
      isMounted = false
    }
  }, [])

  return {
    referralCopiedText,
    dailyReferralText,
    error,
    loading,
    referralAndEnabledDetails,
    referralText,
  }
}
